
import { computed, defineComponent, onMounted, PropType, ref, watch } from 'vue'
import { createNamespacedHelpers } from 'vuex-composition-helpers'

import { UITechpackModel } from '../store/techpack/techpack.types'
import { SharedFolder } from '../store/sharedFolders/sharedFolders.types'
import EmailInput from './EmailInput.vue'

const { useGetters: useSharedFoldersGetters, useActions: useSharedFoldersActions } =
  createNamespacedHelpers('sharedFolders')
const { useGetters: useContactsGetters, useActions: useContactsActions } = createNamespacedHelpers('contacts')

export default defineComponent({
  name: 'AddToSharedFolder',
  props: {
    selectedTechpacks: {
      type: Array as PropType<UITechpackModel[]>,
      required: true
    },
    stepBack: {
      type: Function,
      required: true
    },
    currentUserName: String
  },
  components: { EmailInput },
  setup(props, { emit }) {
    const { getContacts } = useContactsActions(['getContacts'])
    const { emailsInCurrentFolder } = useContactsGetters(['emailsInCurrentFolder'])
    const { getSortedFolders, isLoading } = useSharedFoldersGetters(['getSortedFolders', 'isLoading'])
    const { createSharedFolder, fetchSharedFolders, patchSharedFolder } = useSharedFoldersActions([
      'createSharedFolder',
      'fetchSharedFolders',
      'patchSharedFolder'
    ])

    const selectedFolder = ref<SharedFolder | null>(null)
    const contacts = ref<string[]>([])
    const isCreatingNewFolder = ref(false)
    const isEmailFormValid = ref(false)
    const email = ref(false)
    const newFolderName = ref('')
    const emailSubject = ref('')
    const emailBody = ref('')

    const selectedFolderName = computed(() => {
      return selectedFolder.value?.name || ''
    })

    const techpacksCount = computed(() => {
      return props.selectedTechpacks.length
    })

    const isNewFolderNameless = computed(() => {
      return isCreatingNewFolder.value && !newFolderName.value
    })

    const noEmailsAdded = computed(() => {
      return email.value && !contacts.value.length
    })

    const isButtonDisabled = computed(() => {
      return isNewFolderNameless.value || noEmailsAdded.value || !isEmailFormValid.value
    })

    const buttonText = computed(() => {
      return email.value ? 'Send' : 'Add'
    })

    watch(
      () => getSortedFolders.value,
      folders => {
        if (folders) {
          selectedFolder.value = folders[0]
        } else {
          isCreatingNewFolder.value = true
        }
      }
    )

    watch(
      () => selectedFolder.value,
      folder => {
        const contactIds = folder?.contacts.map(contact => contact.contact)
        contacts.value = emailsInCurrentFolder.value(contactIds)
      }
    )

    watch(
      () => email.value,
      emailVal => {
        if (emailVal) {
          emailSubject.value = `${selectedFolderName.value} - ${techpacksCount.value} tech pack(s) added.`
          emailBody.value = `${props.currentUserName} has added ${techpacksCount.value} new tech pack(s) to a shared folder in Backbone.`
        } else {
          emailSubject.value = ''
          emailBody.value = ''
        }
      }
    )

    onMounted(async () => {
      await fetchSharedFolders()
      await getContacts()
    })

    function handleSelectFolder(value: string) {
      isCreatingNewFolder.value = false
      newFolderName.value = ''
      selectedFolder.value = getSortedFolders.value.find((folder: SharedFolder) => folder._id === value)
    }

    function handleCreateSharedFolder() {
      isCreatingNewFolder.value = true
      selectedFolder.value = null
    }

    async function handleSaveTechpack() {
      isCreatingNewFolder.value = false
      const dto = {
        contacts: contacts.value,
        email: email.value,
        emailBody: emailBody.value,
        emailSubject: emailSubject.value,
        techpacks: props.selectedTechpacks.map((techpack: UITechpackModel) => techpack._id)
      }
      if (newFolderName.value) {
        await createSharedFolder({ ...dto, name: newFolderName.value })
        newFolderName.value = ''
        emit('close-email-techpacks', true)
      } else {
        await patchSharedFolder({ folderId: (selectedFolder.value as SharedFolder)?._id, dto })
        emit('close-email-techpacks', true)
      }
    }

    function emailChange(emailsArray: Array<string>) {
      contacts.value = emailsArray
    }

    function validationChange(e: boolean) {
      isEmailFormValid.value = e
    }

    return {
      email,
      contacts,
      isLoading,
      emailBody,
      buttonText,
      emailChange,
      emailSubject,
      newFolderName,
      getSortedFolders,
      selectedFolder,
      validationChange,
      isButtonDisabled,
      handleSelectFolder,
      handleSaveTechpack,
      isCreatingNewFolder,
      handleCreateSharedFolder
    }
  }
})
